"use client";

import React, { useState, useRef, useEffect } from "react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Separator } from "./ui/separator";
import { createPayIdBeneficiary } from "../services/settingsService";
import { Info, ChevronDown, Check } from "lucide-react";
import ErrorState from "./ErrorState";
import { SecuredFooter } from "./ui/secured-footer";
import loaderVideo from "../assets/images/loader.mp4";
import loaderLogo from "../assets/images/80eight/88-footer-white.svg";
import paySuccessIcon from "../assets/icons/pay_success_icon.svg";
import countriesData from "../assets/countries/data.json";

interface AddBeneficiaryPayIDProps {
  onBack: () => void;
  onSuccess: () => void;
}

type BeneficiaryTabType = "payid" | "email" | "phone";

interface CountryOption {
  name: string;
  code: string;
  flag: string;
  value: string;
  isActive: boolean;
}

interface FormData {
  name: string;
  surname: string;
  payId: string;
  email: string;
  phone: string;
  countryCode: string;
  countryDialCode: string;
}

const AddBeneficiaryPayID: React.FC<AddBeneficiaryPayIDProps> = ({
  onBack,
  onSuccess,
}) => {
  // Countries from data.json
  const countries: CountryOption[] = countriesData.countries;
  const defaultCountry = countries.find((c) => c.code === "ZA") || countries[0];

  const [activeTab, setActiveTab] = useState<BeneficiaryTabType>("payid");
  const [formData, setFormData] = useState<FormData>({
    name: "",
    surname: "",
    payId: "",
    email: "",
    phone: "",
    countryCode: defaultCountry.code,
    countryDialCode: defaultCountry.value,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showFailed, setShowFailed] = useState(false);
  const [failedMessage, setFailedMessage] = useState("");
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowCountryDropdown(false);
        setCountrySearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getCountryByCode = (code: string): CountryOption => {
    return countries.find((c) => c.code === code) || defaultCountry;
  };

  const filteredCountries = countries.filter(
    (c) =>
      c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
      c.value.includes(countrySearch)
  );

  const validatePayId = (value: string): string => {
    if (!value) return "PayID is required";
    // Format: 1 capital letter followed by 6 digits (e.g., A123456)
    const payIdRegex = /^[A-Z][0-9]{6}$/;
    if (!payIdRegex.test(value)) return "Invalid PayID format";
    return "";
  };

  const validateEmail = (value: string): string => {
    if (!value) return "Email is required";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return "Please enter a valid email";
    return "";
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleCountrySelect = (country: CountryOption) => {
    setFormData((prev) => ({
      ...prev,
      countryCode: country.code,
      countryDialCode: country.value,
    }));
    setShowCountryDropdown(false);
    setCountrySearch("");
  };

  const handleSaveContact = async () => {
    let hasErrors = false;
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
      hasErrors = true;
    }
    if (!formData.surname.trim()) {
      newErrors.surname = "Surname is required";
      hasErrors = true;
    }

    if (activeTab === "payid") {
      const payIdError = validatePayId(formData.payId);
      if (payIdError) {
        newErrors.payId = payIdError;
        hasErrors = true;
      }
    }
    if (activeTab === "email") {
      const emailError = validateEmail(formData.email);
      if (emailError) {
        newErrors.email = emailError;
        hasErrors = true;
      }
    }
    if (activeTab === "phone" && !formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
      hasErrors = true;
    }

    if (hasErrors) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);

    try {
      const payload: {
        name: string;
        lastname: string;
        payId?: string;
        email?: string;
        cellphone?: string;
      } = {
        name: formData.name,
        lastname: formData.surname,
      };

      if (activeTab === "payid") {
        payload.payId = formData.payId;
      } else if (activeTab === "email") {
        payload.email = formData.email;
      } else {
        payload.cellphone = `${formData.countryDialCode}${formData.phone}`;
      }

      const response = await createPayIdBeneficiary(payload);

      if (response?.status !== 200 && response?.status !== 201) {
        throw new Error(response?.data?.message || "Failed to add beneficiary");
      }

      setIsLoading(false);
      setShowSuccess(true);
    } catch (err: any) {
      setIsLoading(false);
      const errorMsg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        "";

      // User-friendly error messages
      let friendlyMessage = "Failed to add beneficiary";
      if (errorMsg.toLowerCase().includes("no user found")) {
        if (activeTab === "payid") {
          friendlyMessage =
            "This PayID is not registered in our system. Please check the PayID and try again.";
        } else if (activeTab === "email") {
          friendlyMessage =
            "This email address is not registered in our system. Please check the email and try again.";
        } else {
          friendlyMessage =
            "This phone number is not registered in our system. Please check the number and try again.";
        }
      } else if (errorMsg) {
        friendlyMessage = errorMsg;
      }

      setFailedMessage(friendlyMessage);
      setShowFailed(true);
    }
  };

  const handleDone = () => {
    onSuccess();
  };

  const handleTryAgain = () => {
    setShowFailed(false);
    setFailedMessage("");
  };

  // Loading Screen
  if (isLoading) {
    return (
      <Card className="bg-white rounded-3xl border border-[#EBE9EF] overflow-hidden">
        <CardContent className="p-0">
          <div className="relative w-full h-[600px] flex items-center justify-center">
            {/* Video background */}
            <video
              src={loaderVideo}
              autoPlay
              loop
              muted
              playsInline
              className="w-[500px] h-[500px] object-contain"
            />
            {/* Logo overlay */}
            <img
              src={loaderLogo}
              alt="80eight"
              className="absolute z-10 w-[160px] h-auto top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
            />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Failed Screen
  if (showFailed) {
    return (
      <ErrorState
        title="Adding contact failed"
        message={failedMessage}
        onTryAgain={handleTryAgain}
      />
    );
  }

  // Success Screen
  if (showSuccess) {
    return (
      <Card className="bg-white rounded-3xl border border-[#EBE9EF] overflow-visible">
        <CardContent className="p-0">
          <div className="flex flex-col items-center justify-center py-20 px-8">
            {/* Success Icon */}
            <div className="mb-12">
              <img
                src={paySuccessIcon}
                alt="Success"
                className="w-[194px] h-[136px]"
              />
            </div>

            {/* Title */}
            <h2 className="text-[32px] font-semibold text-[#33125F] mb-6 text-center leading-[40px]">
              Contact Added
            </h2>

            {/* Subtitle */}
            <p className="text-[20px] text-[#423E51] text-center mb-10 leading-[32px] max-w-[338px]">
              {formData.name} {formData.surname} has been added to your
              contacts.
            </p>

            {/* Done Button */}
            <Button
              onClick={handleDone}
              className="h-[48px] w-[302px] rounded-full text-[14px] font-semibold text-white"
              style={{
                background:
                  "linear-gradient(69.77deg, #57249C 10.66%, #EB3366 113.47%)",
              }}
            >
              Done
            </Button>
          </div>
          <SecuredFooter />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white rounded-3xl border border-[#EBE9EF] overflow-visible">
      <CardContent className="p-0">
        {/* Header */}
        <div className="p-8 pb-0">
          <h2 className="text-[20px] font-semibold text-[#33125F] mb-6">
            Add a new beneficiary
          </h2>

          {/* Info Banner */}
          <div className="bg-[#F5F3F8] rounded-full px-6 py-4 flex items-center gap-3 mb-6">
            <Info className="w-5 h-5 text-[#57249C] flex-shrink-0" />
            <p className="text-[14px] text-[#6D6584]">
              This information is required for compliance with global Travel
              Rule regulations.
            </p>
          </div>

          {/* PayID | Email | Phone Toggle */}
          <div className="w-full h-[64px] p-2 bg-transparent border border-[#EBE9EF] rounded-full flex mb-6">
            <button
              onClick={() => setActiveTab("payid")}
              className={`flex-1 h-[48px] rounded-full lg:text-lg text-[14px] font-semibold transition-colors ${
                activeTab === "payid"
                  ? "bg-[#33125F] text-white"
                  : "text-[#6D6584]"
              }`}
            >
              PayID
            </button>
            <button
              onClick={() => setActiveTab("email")}
              className={`flex-1 h-[48px] rounded-full lg:text-lg text-[14px] font-semibold transition-colors ${
                activeTab === "email"
                  ? "bg-[#33125F] text-white"
                  : "text-[#6D6584]"
              }`}
            >
              Email
            </button>
            <button
              onClick={() => setActiveTab("phone")}
              className={`flex-1 h-[48px] rounded-full lg:text-lg text-[14px] font-semibold transition-colors ${
                activeTab === "phone"
                  ? "bg-[#33125F] text-white"
                  : "text-[#6D6584]"
              }`}
            >
              Phone
            </button>
          </div>
        </div>

        {/* Form Fields */}
        <div className="px-8 pb-6">
          {/* For Phone tab: Name & Surname at TOP */}
          {activeTab === "phone" && (
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <p className="text-[14px] text-[#6D6584] font-normal mb-2">
                  Name
                </p>
                <input
                  type="text"
                  placeholder="Name"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  className={`h-[88px] border rounded-2xl w-full px-6 text-[16px] text-[#423E51] placeholder:text-[#6D6584] focus:outline-none focus:ring-2 focus:ring-[#57249C] transition ${
                    errors.name ? "border-[#DA2F58]" : "border-[#CECBD7]"
                  }`}
                />
                {errors.name && (
                  <p className="text-[#DA2F58] text-[13px] mt-1">
                    {errors.name}
                  </p>
                )}
              </div>
              <div>
                <p className="text-[14px] text-[#6D6584] font-normal mb-2">
                  Surname
                </p>
                <input
                  type="text"
                  placeholder="Surname"
                  value={formData.surname}
                  onChange={(e) => handleInputChange("surname", e.target.value)}
                  className={`h-[88px] border rounded-2xl w-full px-6 text-[16px] text-[#423E51] placeholder:text-[#6D6584] focus:outline-none focus:ring-2 focus:ring-[#57249C] transition ${
                    errors.surname ? "border-[#DA2F58]" : "border-[#CECBD7]"
                  }`}
                />
                {errors.surname && (
                  <p className="text-[#DA2F58] text-[13px] mt-1">
                    {errors.surname}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* PayID Tab */}
          {activeTab === "payid" && (
            <div className="mb-6">
              <p className="text-[14px] text-[#6D6584] font-normal mb-2">
                PayID
              </p>
              <input
                type="text"
                placeholder="e.g., A123456"
                value={formData.payId}
                onChange={(e) =>
                  handleInputChange("payId", e.target.value.toUpperCase())
                }
                className={`h-[88px] border rounded-2xl w-full px-6 text-[16px] text-[#423E51] placeholder:text-[#6D6584] focus:outline-none focus:ring-2 focus:ring-[#57249C] transition ${
                  errors.payId ? "border-[#DA2F58]" : "border-[#CECBD7]"
                }`}
              />
              <p className="text-[13px] text-[#6D6584] mt-2">
                Format: 1 capital letter + 6 digits (e.g., A123456)
              </p>
              {errors.payId && (
                <p className="text-[#DA2F58] text-[13px] mt-1">
                  {errors.payId}
                </p>
              )}
            </div>
          )}

          {/* Email Tab */}
          {activeTab === "email" && (
            <div className="mb-6">
              <p className="text-[14px] text-[#6D6584] font-normal mb-2">
                Email
              </p>
              <input
                type="email"
                placeholder="Enter email address"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                className={`h-[88px] border rounded-2xl w-full px-6 text-[16px] text-[#423E51] placeholder:text-[#6D6584] focus:outline-none focus:ring-2 focus:ring-[#57249C] transition ${
                  errors.email ? "border-[#DA2F58]" : "border-[#CECBD7]"
                }`}
              />
              {errors.email && (
                <p className="text-[#DA2F58] text-[13px] mt-1">
                  {errors.email}
                </p>
              )}
            </div>
          )}

          {/* Phone Tab - Country + Phone Row */}
          {activeTab === "phone" && (
            <div className="mb-6">
              <div className="flex gap-4" ref={dropdownRef}>
                {/* Country Dropdown - Fixed width like Figma (197px) */}
                <div className="relative w-[197px] flex-shrink-0">
                  <p className="text-[14px] text-[#6D6584] font-normal mb-2">
                    Country
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCountryDropdown(!showCountryDropdown);
                      setCountrySearch("");
                    }}
                    className={`h-[88px] rounded-2xl w-full px-4 text-left flex items-center justify-between transition ${
                      showCountryDropdown
                        ? "border-2 border-[#57249C]"
                        : "border border-[#CECBD7]"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <img
                        src={getCountryByCode(formData.countryCode).flag}
                        alt={getCountryByCode(formData.countryCode).name}
                        className="w-6 h-6 rounded-sm"
                      />
                      <span className="text-[16px] font-semibold text-[#423E51]">
                        {getCountryByCode(formData.countryCode).value}
                      </span>
                    </div>
                    <ChevronDown
                      className={`w-5 h-5 transition-transform ${
                        showCountryDropdown
                          ? "rotate-180 text-[#57249C]"
                          : "text-[#6D6584]"
                      }`}
                    />
                  </button>
                  {showCountryDropdown && (
                    <div className="absolute top-full left-0 w-[280px] mt-1 bg-white border border-[#CECBD7] rounded-2xl shadow-lg z-50 overflow-hidden">
                      {/* Search input */}
                      <div className="p-3 border-b border-[#EBE9EF]">
                        <input
                          type="text"
                          placeholder="Search country..."
                          value={countrySearch}
                          onChange={(e) => setCountrySearch(e.target.value)}
                          className="w-full px-4 py-2 text-[14px] border border-[#CECBD7] rounded-full outline-none focus:ring-2 focus:ring-[#57249C]"
                          autoFocus
                        />
                      </div>
                      {/* Country list */}
                      <div className="max-h-[240px] overflow-y-auto">
                        {filteredCountries.length > 0 ? (
                          filteredCountries.map((country) => (
                            <button
                              key={country.code}
                              type="button"
                              onClick={() => handleCountrySelect(country)}
                              className="w-full px-4 py-3 text-left text-[14px] text-[#33125F] hover:bg-[#F5F3F8] transition-colors flex items-center justify-between"
                            >
                              <div className="flex items-center gap-3">
                                <img
                                  src={country.flag}
                                  alt={country.name}
                                  className="w-6 h-6 rounded-sm"
                                />
                                <span className="truncate max-w-[140px]">
                                  {country.name}
                                </span>
                                <span className="text-[#6D6584]">
                                  {country.value}
                                </span>
                              </div>
                              {formData.countryCode === country.code && (
                                <Check className="w-5 h-5 text-[#57249C] flex-shrink-0" />
                              )}
                            </button>
                          ))
                        ) : (
                          <p className="px-4 py-3 text-[14px] text-[#6D6584]">
                            No results found
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Phone Input - Fills remaining space */}
                <div className="flex-1">
                  <p className="text-[14px] text-[#6D6584] font-normal mb-2">
                    Phone
                  </p>
                  <input
                    type="tel"
                    placeholder="Enter phone number"
                    value={formData.phone}
                    onChange={(e) =>
                      handleInputChange(
                        "phone",
                        e.target.value.replace(/[^0-9]/g, "")
                      )
                    }
                    className={`h-[88px] border rounded-2xl w-full px-6 text-[16px] text-[#423E51] placeholder:text-[#6D6584] focus:outline-none focus:ring-2 focus:ring-[#57249C] transition ${
                      errors.phone ? "border-[#DA2F58]" : "border-[#CECBD7]"
                    }`}
                  />
                </div>
              </div>
              {errors.phone && (
                <p className="text-[#DA2F58] text-[13px] mt-1">
                  {errors.phone}
                </p>
              )}
            </div>
          )}

          {/* Name & Surname Row - For PayID and Email tabs (at bottom) */}
          {activeTab !== "phone" && (
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <p className="text-[14px] text-[#6D6584] font-normal mb-2">
                  Name
                </p>
                <input
                  type="text"
                  placeholder="Name"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  className={`h-[88px] border rounded-2xl w-full px-6 text-[16px] text-[#423E51] placeholder:text-[#6D6584] focus:outline-none focus:ring-2 focus:ring-[#57249C] transition ${
                    errors.name ? "border-[#DA2F58]" : "border-[#CECBD7]"
                  }`}
                />
                {errors.name && (
                  <p className="text-[#DA2F58] text-[13px] mt-1">
                    {errors.name}
                  </p>
                )}
              </div>
              <div>
                <p className="text-[14px] text-[#6D6584] font-normal mb-2">
                  Surname
                </p>
                <input
                  type="text"
                  placeholder="Surname"
                  value={formData.surname}
                  onChange={(e) => handleInputChange("surname", e.target.value)}
                  className={`h-[88px] border rounded-2xl w-full px-6 text-[16px] text-[#423E51] placeholder:text-[#6D6584] focus:outline-none focus:ring-2 focus:ring-[#57249C] transition ${
                    errors.surname ? "border-[#DA2F58]" : "border-[#CECBD7]"
                  }`}
                />
                {errors.surname && (
                  <p className="text-[#DA2F58] text-[13px] mt-1">
                    {errors.surname}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        <Separator className="bg-[#EBE9EF]" />

        {/* Action Buttons */}
        <div className="p-8 flex gap-4">
          <Button
            onClick={onBack}
            className="flex-1 h-[48px] bg-white border border-[#EBE9EF] text-[#6D6584] text-[14px] font-semibold rounded-full hover:bg-[#F5F3F8] transition-colors"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveContact}
            className="flex-1 h-[48px] rounded-full text-[14px] font-semibold text-white"
            style={{
              background:
                "linear-gradient(69.77deg, #57249C 10.66%, #EB3366 113.47%)",
            }}
          >
            Save contact
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default AddBeneficiaryPayID;
